'use client';

import { PropsWithChildren, useMemo } from 'react';
import {
  Box,
  ChakraProvider,
  extendTheme,
  ThemeOverride,
} from '@chakra-ui/react';
import { CacheProvider } from '@chakra-ui/next-js';
import dynamic from 'next/dynamic';

import { ThemeColors, baseTheme, fallbackTheme } from 'styles/theme';
import { useAlertDialog } from 'context/alertDialog';
import { lighten } from 'utils/helpers';

const AlertDialog = dynamic(() => import('../AlertDialog/AlertDialog'), {
  ssr: false,
});

interface Props extends PropsWithChildren {
  themeColors: ThemeColors;
}

export type ThemeProviderProps = Props;

export const ThemeProvider = (props: Props) => {
  const { children, themeColors } = props;

  const { alertDialogProps, onClose } = useAlertDialog();

  const theme = useMemo(() => {
    if (!themeColors) return fallbackTheme;

    const _theme: ThemeOverride = {
      ...baseTheme,
      colors: { ...baseTheme.colors, ...themeColors },
      shadows: {
        ...baseTheme.shadows,
        'giftcards-pages-main': `0 0 20px ${lighten(
          themeColors.dynamic.base,
          0.35,
        )}`,
        'shadow-button': `0 0 10px ${lighten(themeColors.dynamic.base, 0.45)}`,
      },
    };

    return extendTheme(_theme);
  }, [themeColors]);

  return (
    <CacheProvider>
      <ChakraProvider theme={theme}>
        {alertDialogProps?.isOpen && (
          <AlertDialog {...alertDialogProps} onClose={onClose} />
        )}

        <Box h="100vh" w="100vw">
          {children}
        </Box>
      </ChakraProvider>
    </CacheProvider>
  );
};
